import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Snackbar,
  Alert,
  MenuItem,
  Stack,
  Divider,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
  collection,
  addDoc,
  getDocs,
  Timestamp,
  query,
  where,
} from "firebase/firestore";
import { db, auth } from "../../firebase";

// Utility: returns total payroll for a given list
const getTotal = (items) =>
  items.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);

export default function RecordPayroll() {
  const [employees, setEmployees] = useState([]);
  const [employeeId, setEmployeeId] = useState("");
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(new Date());
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [notes, setNotes] = useState("");
  const [snackbar, setSnackbar] = useState(false);
  const [payrollRecords, setPayrollRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  // DatePicker control state
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  // Payroll totals for this month/year
  const thisMonth = new Date().getMonth() + 1;
  const thisYear = new Date().getFullYear();
  const monthlyPayroll = useMemo(
    () =>
      getTotal(
        payrollRecords.filter((r) => {
          const d = r.createdAt?.toDate?.() || new Date(r.createdAt);
          return (
            d && d.getMonth() + 1 === thisMonth && d.getFullYear() === thisYear
          );
        }),
      ),
    [payrollRecords, thisMonth, thisYear],
  );
  const yearlyPayroll = useMemo(
    () =>
      getTotal(
        payrollRecords.filter((r) => {
          const d = r.createdAt?.toDate?.() || new Date(r.createdAt);
          return d && d.getFullYear() === thisYear;
        }),
      ),
    [payrollRecords, thisYear],
  );

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      const user = auth.currentUser;
      if (!user) return;

      const empQ = query(
        collection(db, "employees"),
        where("uid", "==", user.uid),
      );
      const empSnap = await getDocs(empQ);
      setEmployees(empSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() })));

      const projQ = query(
        collection(db, "projects"),
        where("uid", "==", user.uid),
      );
      const projSnap = await getDocs(projQ);
      setProjects(projSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() })));

      // Fetch payroll records for summary cards
      const payQ = query(
        collection(db, "payroll"),
        where("uid", "==", user.uid),
      );
      const paySnap = await getDocs(payQ);
      setPayrollRecords(paySnap.docs.map((doc) => ({ ...doc.data() })));
      setLoading(false);
    };
    fetchAll();
  }, []);

  const handleSubmit = async () => {
    if (!employeeId || !amount) return;

    const selected = employees.find((e) => e.id === employeeId);
    await addDoc(collection(db, "payroll"), {
      uid: auth.currentUser.uid,
      employeeId,
      name: selected?.name || "Unknown",
      amount: parseFloat(amount),
      projectId: projectId || null,
      notes: notes || "",
      type: "payroll",
      createdAt: Timestamp.fromDate(date),
    });

    setAmount("");
    setEmployeeId("");
    setDate(new Date());
    setNotes("");
    setSnackbar(true);

    // Refresh payroll totals after submission
    const payQ = query(
      collection(db, "payroll"),
      where("uid", "==", auth.currentUser.uid),
    );
    const paySnap = await getDocs(payQ);
    setPayrollRecords(paySnap.docs.map((doc) => ({ ...doc.data() })));
  };

  // Allow input field to open the date picker
  const handleDateInputClick = (e) => {
    // Prevent the calendar icon's built-in click (which already opens it)
    if (
      e.target &&
      e.target.getAttribute("role") === "button" &&
      e.target.getAttribute("aria-label") === "Choose date"
    ) {
      return;
    }
    setDatePickerOpen(true);
  };

  return (
    <Paper sx={{ p: { xs: 2, md: 4 }, maxWidth: 520, mx: "auto" }}>
      <Typography variant="h6" fontWeight={700} gutterBottom>
        Record Payroll Payment
      </Typography>

      {/* Summary Cards */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        mb={3}
        mt={2}
        alignItems="stretch"
      >
        <Paper
          elevation={1}
          sx={{
            flex: 1,
            p: 2,
            background: "linear-gradient(90deg, #dbeafe 0%, #f0abfc 100%)",
            color: "#0f172a",
            borderRadius: 2,
            minWidth: 160,
            mb: { xs: 0.5, sm: 0 },
          }}
        >
          <Typography
            variant="body2"
            fontWeight={600}
            mb={0.3}
            sx={{ opacity: 0.8 }}
          >
            <span role="img" aria-label="month">
              📅
            </span>{" "}
            This Month
          </Typography>
          <Typography variant="h5" fontWeight={700}>
            €
            {monthlyPayroll.toLocaleString(undefined, {
              minimumFractionDigits: 2,
            })}
          </Typography>
        </Paper>
        <Paper
          elevation={1}
          sx={{
            flex: 1,
            p: 2,
            background: "linear-gradient(90deg, #e0e7ff 0%, #a7f3d0 100%)",
            color: "#0f172a",
            borderRadius: 2,
            minWidth: 160,
          }}
        >
          <Typography
            variant="body2"
            fontWeight={600}
            mb={0.3}
            sx={{ opacity: 0.8 }}
          >
            <span role="img" aria-label="year">
              🗓️
            </span>{" "}
            This Year
          </Typography>
          <Typography variant="h5" fontWeight={700}>
            €
            {yearlyPayroll.toLocaleString(undefined, {
              minimumFractionDigits: 2,
            })}
          </Typography>
        </Paper>
      </Stack>

      <Divider sx={{ my: 2 }} />

      {loading ? (
        <Typography variant="body2" color="text.secondary">
          Loading...
        </Typography>
      ) : employees.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          No employees found. Please add employees under the "Employees" tab
          first.
        </Typography>
      ) : (
        <Box component="form" noValidate>
          <TextField
            select
            label="Select Employee"
            fullWidth
            value={employeeId}
            onChange={(e) => setEmployeeId(e.target.value)}
            sx={{ mb: 2 }}
          >
            {employees.map((emp) => (
              <MenuItem key={emp.id} value={emp.id}>
                {emp.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Amount (€)"
            type="number"
            fullWidth
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            sx={{ mb: 2 }}
          />

          <TextField
            select
            label="Link to Project (optional)"
            fullWidth
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
            sx={{ mb: 2 }}
          >
            <MenuItem value="">None</MenuItem>
            {projects.map((proj) => (
              <MenuItem key={proj.id} value={proj.id}>
                {proj.name}
              </MenuItem>
            ))}
          </TextField>

          {/* Date Picker Field - Fully clickable input */}
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Payment Date"
              open={datePickerOpen}
              onOpen={() => setDatePickerOpen(true)}
              onClose={() => setDatePickerOpen(false)}
              value={date}
              onChange={(newDate) => {
                setDate(newDate);
                setDatePickerOpen(false);
              }}
              slotProps={{
                textField: {
                  fullWidth: true,
                  InputProps: {
                    readOnly: true,
                    sx: { cursor: "pointer" },
                    onClick: handleDateInputClick,
                  },
                },
              }}
              disableFuture={false}
              sx={{ mb: 2, width: "100%" }}
            />
          </LocalizationProvider>

          <TextField
            label="Notes (Optional)"
            multiline
            minRows={2}
            fullWidth
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            sx={{ mb: 2 }}
          />

          <Button
            variant="contained"
            onClick={handleSubmit}
            fullWidth
            size="large"
            sx={{ mt: 1 }}
            disabled={!employeeId || !amount}
          >
            ✅ Record Payment
          </Button>
        </Box>
      )}

      <Snackbar
        open={snackbar}
        autoHideDuration={4000}
        onClose={() => setSnackbar(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity="success" onClose={() => setSnackbar(false)}>
          Payment recorded successfully
        </Alert>
      </Snackbar>
    </Paper>
  );
}
